import { Router, ActivatedRoute } from '@angular/router';
import { Component, inject, signal, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { routeNames } from '../../../../shared/route-names';
import { Oauth2Service } from '../../services/oauth2.service';
import { UserService } from '../../../user/services/user.service';
import { Oauth2Provider } from '../../../../models/provider.model';
import { ReturnUrlService } from '../../services/return-url.service';

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  private _userService = inject(UserService);
  private _oauth2Service = inject(Oauth2Service);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _translateService = inject(TranslateService);
  private _returnUrlService = inject(ReturnUrlService);

  providers = signal<Oauth2Provider[]>([]);
  errorMessage = signal<string>('');
  isLoading = signal<boolean>(true);

  ngOnInit() {
    // Check if already logged in (has valid token and user data)
    if (this._userService.isLoggedIn()) {
      this.navigateToReturnUrlOrHome();
      return;
    }

    // Handle error messages from query params
    this._route.queryParams.subscribe(params => {
      if (params['error']) {
        this.setErrorMessage(params['error']);
        this.isLoading.set(false);
      } else {
        // No error, check if we have a token to validate
        if (this._userService.hasToken()) {
          // We have a token, try to validate it by fetching user
          this.validateTokenAndProceed();
        } else {
          // No token, proceed with OAuth2 login
          this.initiateAutoLogin();
        }
      }
    });
  }

  private navigateToReturnUrlOrHome() {
    const returnUrl = this._returnUrlService.getReturnUrlOrDefault(`${routeNames.root + routeNames.home}`);
    this._router.navigateByUrl(returnUrl);
  }

  private validateTokenAndProceed() {
    this._userService.fetchUser().subscribe({
      next: () => {
        // Token is valid, navigate to return URL or home
        this.navigateToReturnUrlOrHome();
      },
      error: () => {
        // Token is invalid, clear it and proceed with OAuth2 login
        localStorage.removeItem('token');
        this.initiateAutoLogin();
      }
    });
  }

  private setErrorMessage(errorCode: string) {
    setTimeout(() => {
      const errorMessages: Record<string, string> = {
        'access_denied': this._translateService.instant('auth.errors.access_denied'),
        'auth_failed': this._translateService.instant('auth.errors.auth_failed'),
        'no_token': this._translateService.instant('auth.errors.token_missing'),
        'user_fetch_failed': this._translateService.instant('auth.errors.user_fetch_failed'),
        'callback_failed': this._translateService.instant('auth.errors.callback_failed')
      };
      this.errorMessage.set(errorMessages[errorCode] || this._translateService.instant('auth.errors.unknown'));
    }, 200);
  }

  private initiateAutoLogin() {
    this._oauth2Service.getProviders().subscribe({
      next: (providers: Oauth2Provider[]) => {
        this.providers.set(providers);

        if (providers.length === 1) {
          // Auto-redirect immediately if only one provider
          setTimeout(() => {
            this.redirect(providers[0].url);
          }, 1000); // Small delay to show loading state
        } else if (providers.length > 1) {
          // Show provider selection
          this.isLoading.set(false);
        }
      },
      error: (error) => {
        console.error('Failed to load providers:', error);
        this.errorMessage.set(this._translateService.instant('auth.errors.providers_load_failed'));
        this.isLoading.set(false);
      }
    });
  }

  public retryLogin() {
    this.errorMessage.set('');
    this.isLoading.set(true);
    // Clear error from URL
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {},
      replaceUrl: true
    });
    this.initiateAutoLogin();
  }

  public loginWithOAuth2(url: string) {
    this.redirect(url);
  }

  public redirect(url: string) {
    window.location.href = url;
  }
}
